/**
 * Admin — Revenue Calculation, product economics table (card 1243).
 *
 * One EXPANDABLE row per product; expanding reveals that product's dosage × supply-term
 * rows with every column named in ADMIN-PAGE-PLAN.md §Product-level table.
 *
 * 🔑 THREE STATES, NEVER TWO. A money cell is either a figure (`$x` — a measured $0.00 is a
 * FACT and is kept), a blocker (`— / Missing` or `— / Conflicting`), or a non-offer
 * (`Not sold`). Folding "not sold" into "missing" would be wrong twice: it is not a gap
 * anyone can fill, and it would inflate the missing-cost exposure card 1245 must total.
 * Folding it into `$0` is the exact failure this project exists to prevent.
 *
 * 🔑 EVERY TERM ROW IS RENDERED — all 565, not just the 259 sold. The API's own `count` is
 * the audit: a table showing only sold terms would report 259 and read as 306 dropped rows.
 *
 * 🔑 NO ARITHMETIC IS INVENTED HERE. Contribution margin and the fully-loaded pair come from
 * utils/revenueMetrics, which mirrors the server's own subtractMetrics/toMargin rules.
 */

import { useState, useMemo, useCallback } from 'react';
import {
  formatMetricValue,
  isBlockedMetric,
  describeMetricBasis,
  deriveContributionMargin,
  deriveFullyLoadedProfit,
  deriveFullyLoadedMargin,
  groupRowsByProduct,
  NOT_SOLD_TEXT
} from '../../utils/revenueMetrics';
import { formatPrice } from '../../utils/formatters';
import RevenueTimeline from './RevenueTimeline';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';

/**
 * The money columns of §Product-level table, in the plan's own order. Pure data — the
 * detail table maps this list, so the column set is one readable thing rather than markup
 * repeated fourteen times. `profit` is the API's name for contribution profit.
 */
export const MONEY_COLUMNS = Object.freeze([
  { key: 'grossRevenue', label: 'Gross revenue' },
  { key: 'netSettledRevenue', label: 'Net settled revenue' },
  { key: 'medicationCost', label: 'Medication cost' },
  { key: 'ancillaryCost', label: 'Ancillary / syringe / cold-chain' },
  { key: 'shippingCost', label: 'Shipping cost' },
  { key: 'consultationCost', label: 'Consultation cost' },
  { key: 'reserveWithheld', label: 'Reserve withheld' },
  { key: 'staticProviderExpenseAllocated', label: 'Allocated static provider expense' },
  { key: 'totalVariableExpense', label: 'Total variable expense' },
  { key: 'profit', label: 'Contribution profit' },
  { key: 'contributionMargin', label: 'Contribution margin', kind: 'percent' },
  { key: 'fullyLoadedProfit', label: 'Fully-loaded profit' },
  { key: 'fullyLoadedMargin', label: 'Fully-loaded margin', kind: 'percent' }
]);

const QUALITY_LABELS = Object.freeze({
  verified: 'Verified',
  estimated: 'Estimated',
  missing: 'Missing',
  conflicting: 'Conflicting'
});

/**
 * Card 3657. How the cost was matched to its source, in words. The raw vocabulary is the API's
 * and stays in `data-match-kind` for tests and filtering; only the DISPLAY is humanised here.
 */
const MATCH_KIND_LABELS = Object.freeze({
  'registry-mapped': 'registry mapping',
  'exact-dosage': 'this dosage',
  'lowest-dosage-fallback': 'lowest dosage on product',
  'exact-sku': 'exact vendor SKU',
  'exact-name-strength': 'vendor name + strength'
});

/** Human labels for the readings a CONFLICTING metric carries, so both are legible. */
const READING_LABELS = Object.freeze({
  ourCostWholeTerm: 'whole-term (code)',
  ourCostPerMonthScaled: 'per-month × months (data)',
  perMonthInternalCostService: 'per-month (engine)'
});

/** Pure. The four data-quality states, visually distinct. */
const QualityChip = ({ quality }) => (
  <span className={styles.qualityChip} data-quality={quality}>
    {QUALITY_LABELS[quality] ?? quality ?? 'Unknown'}
  </span>
);

/**
 * Pure. The whole row for one product × dosage × term, keyed by column. Adds the three
 * client-derived columns to the API row so MONEY_COLUMNS can be a flat lookup.
 */
export const toMetricsByColumn = (row) => ({
  ...row,
  contributionMargin: deriveContributionMargin(row),
  fullyLoadedProfit: deriveFullyLoadedProfit(row),
  fullyLoadedMargin: deriveFullyLoadedMargin(row)
});

/**
 * Pure. One money cell. `isOffered:false` short-circuits BEFORE any formatting, so an
 * unsold term can never reach the blocked-value path and be mislabelled as a blocker.
 */
const MetricCell = ({ metric, column, isOffered }) => {
  if (!isOffered) {
    return (
      <td className={styles.moneyCell} data-col={column.key} data-offered="false">
        <span className={styles.notSold}>{NOT_SOLD_TEXT}</span>
      </td>
    );
  }
  const blocked = isBlockedMetric(metric);
  const readings = Object.entries(metric?.readings ?? metric?.engineReading ?? {});
  return (
    <td
      className={styles.moneyCell}
      data-col={column.key}
      data-offered="true"
      data-quality={metric?.dataQuality}
      data-blocked={String(blocked)}
      title={describeMetricBasis(metric)}
    >
      <span className={blocked ? styles.blockedValue : styles.metricValue}>
        {formatMetricValue(metric, column.kind)}
      </span>
      {readings.length > 0 && (
        <span className={styles.readings} data-testid="metric-readings">
          {readings.map(([name, value]) => (
            <span key={name} className={styles.reading}>
              {formatPrice(Number(value))} <em>{READING_LABELS[name] ?? name}</em>
            </span>
          ))}
        </span>
      )}
      <MetricProvenance metric={metric} />
      <QualityChip quality={metric?.dataQuality} />
    </td>
  );
};

/**
 * Pure. Card 3657 — the PROVENANCE of a money line: the artifact that evidences the figure and
 * how it was matched to this row. Both facts already travelled on the API and neither was ever
 * rendered, so a reader could see `$29.00 Estimated` with no way to ask "on whose authority".
 *
 * ⛔ RENDERS NOTHING WHEN THERE IS NOTHING TO CITE. An unknown source is `null`, and a `null`
 * must not become an empty chip — a citation with a blank name reads as a source that exists and
 * has no name, which is a stronger and falser claim than silence. Same reason the attribute is
 * left OFF the element rather than set to `""`.
 *
 * This keys off the METRIC, not off a column name, so any line that gains a source renders it
 * without a second edit here.
 */
const MetricProvenance = ({ metric }) => {
  const sourceFile = metric?.sourceFile ?? null;
  const matchKind = metric?.matchKind ?? null;
  if (sourceFile === null && matchKind === null) return null;
  return (
    <span
      className={styles.provenance}
      data-testid="metric-provenance"
      data-source-file={sourceFile ?? undefined}
      data-match-kind={matchKind ?? undefined}
    >
      {sourceFile !== null && <span className={styles.sourceFile}>{sourceFile}</span>}
      {matchKind !== null && <em className={styles.matchKind}>{MATCH_KIND_LABELS[matchKind] ?? matchKind}</em>}
    </span>
  );
};

/** Pure. One dosage × term detail row. */
const TermRow = ({ row }) => {
  const metrics = toMetricsByColumn(row);
  const isOffered = Boolean(row?.term?.isOffered);
  return (
    <tr
      className={styles.termRow}
      data-testid="term-row"
      data-term-key={row?.term?.key}
      data-offered={String(isOffered)}
    >
      <td className={styles.termCellDosage} data-col="dosage">
        {row?.dosageAmount ?? <span className={styles.notSold}>No dosage recorded</span>}
      </td>
      <td className={styles.termCell} data-col="term" data-internal-months={row?.term?.months}>
        <span className={styles.termLabel}>{row?.term?.label}</span>
        <span className={styles.termMonths}>{row?.term?.months} internal months</span>
        {row?.term?.isSupported === false && (
          <span className={styles.termDefect}>unsupported storage key &quot;{row?.term?.key}&quot;</span>
        )}
      </td>
      {MONEY_COLUMNS.map((column) => (
        <MetricCell key={column.key} column={column} metric={metrics[column.key]} isOffered={isOffered} />
      ))}
      {/* Card 54165. Deliberately no `styles.qualityCell`: the class was never declared in
          `RevenueCalculationPage.module.css`, so this resolved to `undefined`. A cell rule
          could not apply here anyway — `.detailTable td` sets `min-width: 0` at class+element
          specificity, which is why the sibling `.moneyCell`'s own `min-width: 9rem` likewise
          computes to 0px. The base `td` rule already supplies this cell's padding, border,
          wrapping and alignment, and `data-col` is the targeting hook. */}
      <td data-col="dataQuality">
        {isOffered
          ? <QualityChip quality={row?.dataQuality} />
          : <span className={styles.notSold}>{NOT_SOLD_TEXT}</span>}
      </td>
    </tr>
  );
};

/**
 * Pure. A product's expanded detail: every one of its dosage × term rows, in a horizontally
 * scrollable container so the nineteen columns stay readable on a 768px screen.
 */
const ProductDetail = ({ group }) => (
  <div className={styles.detailScroll} data-testid={`product-detail-${group.productId}`}>
    <table className={styles.detailTable}>
      <thead>
        <tr>
          <th scope="col">Dosage / SKU</th>
          <th scope="col">Supply term</th>
          {MONEY_COLUMNS.map((column) => <th scope="col" key={column.key}>{column.label}</th>)}
          <th scope="col">Data quality</th>
        </tr>
      </thead>
      <tbody>
        {group.termRows.map((row) => (
          <TermRow key={`${row.dosageId ?? 'none'}-${row.term.key}`} row={row} />
        ))}
      </tbody>
    </table>
  </div>
);

/** Pure. The collapsed product row plus, when expanded, its detail row. */
const ProductRow = ({ group, isExpanded, onToggle }) => (
  <>
    <tr
      className={styles.productRow}
      data-testid="product-row"
      data-product-id={group.productId}
      data-term-rows={group.termRowCount}
      data-quality={group.dataQuality}
      data-expanded={String(isExpanded)}
    >
      <td className={styles.expanderCell}>
        <button
          type="button"
          className={styles.expander}
          data-testid="product-expander"
          aria-expanded={isExpanded}
          aria-controls={`product-detail-${group.productId}`}
          onClick={() => onToggle(group.productId)}
        >
          <span aria-hidden="true">{isExpanded ? '−' : '+'}</span>
          <span className={styles.srOnly}>
            {isExpanded ? 'Collapse' : 'Expand'} {group.productName}
          </span>
        </button>
      </td>
      <th scope="row" className={styles.productCell}>{group.productName}</th>
      <td>{group.category ?? <span className={styles.notSold}>Uncategorised</span>}</td>
      <td data-col="provider">{group.providerType ?? <span className={styles.notSold}>Unrecorded</span>}</td>
      <td data-col="status">
        <span className={styles.statusChip} data-active={String(group.isActive)}>
          {group.isActive ? 'Active' : 'Inactive'}
        </span>
      </td>
      <td className={styles.numberCell}>{group.dosageCount}</td>
      <td className={styles.termsSoldCell} data-col="termsSold">
        {group.offeredTermLabels.length > 0
          ? group.offeredTermLabels.map((label) => (
            <span key={label} className={styles.termChip}>{label}</span>
          ))
          : <span className={styles.notSold}>No term sold</span>}
      </td>
      <td className={styles.numberCell}>{group.termRowCount}</td>
      <td><QualityChip quality={group.dataQuality} /></td>
    </tr>
    {isExpanded && (
      <tr className={styles.detailRow}>
        {/* Card 1244 — timeline is a SIBLING of the 64rem scroller, never inside it. */}
        <td colSpan={9}>
          <RevenueTimeline productId={group.productId} />
          <ProductDetail group={group} />
        </td>
      </tr>
    )}
  </>
);

const RevenueProductTable = ({ rows, apiCount, apiProductCount }) => {
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const groups = useMemo(() => groupRowsByProduct(rows), [rows]);

  // Immutable toggle — the expanded set is the ONLY state, so collapse/re-expand rebuilds
  // identical content from props by construction rather than by careful bookkeeping.
  const handleToggle = useCallback((productId) => {
    setExpandedIds((current) => {
      const next = new Set(current);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }, []);

  const termRowCount = groups.reduce((total, group) => total + group.termRowCount, 0);

  return (
    <div
      className={styles.tableWrap}
      data-testid="product-economics-table"
      data-product-count={groups.length}
      data-term-row-count={termRowCount}
      data-api-count={apiCount ?? ''}
      data-api-product-count={apiProductCount ?? ''}
    >
      <p className={styles.tableScope} data-testid="table-scope">
        {groups.length} products · {termRowCount} rendered of {apiCount ?? termRowCount} API
        rows. Inactive and economically incomplete products are listed, never dropped.
      </p>
      <div className={styles.tableScroll}>
        <table className={styles.productTable}>
          <thead>
            <tr>
              <th scope="col"><span className={styles.srOnly}>Expand</span></th>
              <th scope="col">Product</th>
              <th scope="col">Category</th>
              <th scope="col">Provider</th>
              <th scope="col">Status</th>
              <th scope="col">Dosages</th>
              <th scope="col">Terms sold</th>
              <th scope="col">Term rows</th>
              <th scope="col">Data quality</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => (
              <ProductRow
                key={group.productId}
                group={group}
                isExpanded={expandedIds.has(group.productId)}
                onToggle={handleToggle}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RevenueProductTable;
